import { LoginResponse } from '@mightybyte/business';
import {
    MB_accessTokenUtils,
    Token,
} from '@mightybyte/rnw2.utils.access-token-utils';

const API_URL = () => import.meta.env.VITE_API_URL || 'http://localhost:4000';

/**
 * Calls the token refresh endpoint and stores the new token pair.
 * Throws if the refresh fails (caller is responsible for signing out).
 */
export async function forceRefreshToken(): Promise<void> {
    const tokenStr = await MB_accessTokenUtils.getAccessToken();
    if (!tokenStr) throw new Error('No token stored');

    const parsed = JSON.parse(tokenStr) as Token;
    const refreshToken = parsed.refreshToken;

    const response = await fetch(`${API_URL()}/api/users/token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: refreshToken,
        },
        credentials: 'include',
        body: JSON.stringify({
            refreshToken: refreshToken.replace('Bearer ', ''),
        }),
    });

    if (!response.ok) {
        throw new Error('Token refresh failed');
    }

    const data = await response.json();
    if (data.status === 'success' && data.data) {
        await MB_accessTokenUtils.setAccessToken({
            accessToken: data.data.tokenPair.accessToken,
            refreshToken: data.data.tokenPair.refreshToken,
        });
        return;
    }

    throw new Error('Invalid token refresh response');
}

// Initialize the refresh token endpoint function
const initializeTokenUtils = () => {
    MB_accessTokenUtils.setAccessTokenRefreshEndpointFunction(
        async (
            accessToken: string,
            refreshToken: string,
            skipThrows?: boolean,
        ) => {
            const response = await fetch(`${API_URL()}/api/users/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: refreshToken, // Already has "Bearer " prefix
                },
                credentials: 'include',
                body: JSON.stringify({
                    refreshToken: refreshToken.replace('Bearer ', ''),
                }),
            });

            if (!response.ok) {
                if (skipThrows) {
                    throw new Error('Token refresh failed');
                }
                // Clear tokens and redirect to login
                await MB_accessTokenUtils.removeAccessToken();
                window.location.href = '/login';
                throw new Error('Token refresh failed');
            }

            const data = await response.json();
            if (data.status === 'success' && data.data) {
                // Return token in the format MB_accessTokenUtils expects
                return {
                    accessToken: data.data.tokenPair.accessToken,
                    refreshToken: data.data.tokenPair.refreshToken,
                };
            }

            throw new Error('Invalid token refresh response');
        },
    );
};

// Initialize on module load
if (typeof window !== 'undefined') {
    initializeTokenUtils();
}

export async function setAuthTokens(
    loginResponse: LoginResponse,
): Promise<void> {
    const token: Token = {
        accessToken: loginResponse.tokenPair.accessToken,
        refreshToken: loginResponse.tokenPair.refreshToken,
    };

    await MB_accessTokenUtils.setAccessToken(token);
}

export async function getAccessToken(): Promise<string | undefined> {
    try {
        return await MB_accessTokenUtils.getAccessToken();
    } catch (error) {
        console.error('Error getting access token:', error);
        return undefined;
    }
}

export async function clearAuthTokens(): Promise<void> {
    await MB_accessTokenUtils.removeAccessToken();
}

export function openGoogleLogin(): void {
    if (typeof window === 'undefined') return;

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
    const loginUrl = `${apiUrl}/api/users/login/google`;

    // Open popup window for OAuth
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    window.open(
        loginUrl,
        'Google Login',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes`,
    );
}
